/** Display names for Hmong Village save ids on the admin user page. */

const CROPS: Record<string, string> = {
  nplej: 'Rice',
  zaub: 'Vegetables',
  'pob-kws': 'Corn',
  'kua-txob': 'Chili pepper',
  taum: 'Green bean',
  taub: 'Pumpkin',
  'txiv-lws': 'Tomato',
};

const FISH: Record<string, string> = {
  minnow: 'Minnow',
  carp: 'Carp',
  catfish: 'Catfish',
  perch: 'Perch',
  gourami: 'Gourami',
  loach: 'Loach',
  snakehead: 'Snakehead',
  'freshwater-eel': 'Freshwater eel',
  knifefish: 'Knifefish',
  'freshwater-stingray': 'Freshwater stingray',
  betta: 'Fighting fish',
  'golden-carp': 'Golden carp',
  'ancient-golden-carp': 'Ancient golden carp',
  'giant-river-catfish': 'Giant river catfish',
  'giant-snakehead': 'Giant snakehead',
  'spotted-knifefish': 'Spotted knifefish',
  'moonlight-knifefish': 'Moonlight knifefish',
  'giant-freshwater-stingray': 'Giant freshwater stingray',
  'rainbow-stingray': 'Rainbow stingray',
};

const FORAGE: Record<string, string> = {
  'txiv-pos': 'Berries',
  'txiv-ntoo': 'Fruit',
};

const GOODS: Record<string, string> = {
  'ore:av': 'Dirt',
  'ore:zeb': 'Stone',
  'gem:emerald': 'Emerald',
};

const QUESTS: Record<string, string> = {
  'quest-catch-fish': 'Catch 3 fish for Pog',
  'quest-qaib': 'Find the chicken',
  'quest-npua': 'Find the pig',
  'quest-twm': 'Find the water buffalo',
  'quest-harvest': 'Harvest 3 crops',
  'quest-berries': 'Bring Maiv 3 berries',
  'quest-zaub': 'Bring Npawg 2 vegetables',
};

const NPCS: Record<string, string> = {
  pog: 'Pog (Grandma)',
  yawg: 'Yawg (Grandpa)',
  niam: 'Niam (Mom)',
  txiv: 'Txiv ntxawm (Uncle)',
  tub: 'Kwv (Little brother)',
  maiv: 'Maiv',
  npawg: 'Npawg (Cousin)',
};

const AREAS: Record<string, string> = {
  town: 'Town',
  farm: 'Farm',
  passage: 'Passage',
  mountain: 'Mountain',
};

const XP_SOURCES: Record<string, string> = {
  word: 'Words',
  quest: 'Quests',
  harvest: 'Harvest',
  catch: 'Fishing',
  mine: 'Mining',
  forage: 'Forage',
  gift: 'Gifts',
  daily_objective: 'Daily objectives',
  all_quests: 'All quests',
};

const PLOT_STATUS: Record<string, string> = {
  ready: 'Ready to harvest',
  growing: 'Growing',
  dry: 'Needs water',
  wilted: 'Wilted',
  tilled: 'Tilled, empty',
  untilled: 'Untilled',
};

function humanize(id: string): string {
  const text = id.replace(/[_:]+/g, ' ').replace(/-/g, ' ').trim();
  if (!text) return id;
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function villageActivityLabel(id: string | null | undefined): string {
  if (!id) return '—';
  if (id.startsWith('seed:')) return `${villageActivityLabel(id.slice(5))} seeds`;
  if (id.startsWith('produce:')) return villageActivityLabel(id.slice('produce:'.length));
  if (id.startsWith('fish:')) return villageActivityLabel(id.slice('fish:'.length));
  if (id.startsWith('forage:')) {
    const word = id.slice('forage:'.length);
    return FORAGE[word] ?? villageActivityLabel(word);
  }
  return (
    CROPS[id] ??
    FISH[id] ??
    FORAGE[id] ??
    GOODS[id] ??
    QUESTS[id] ??
    NPCS[id] ??
    AREAS[id] ??
    XP_SOURCES[id] ??
    humanize(id)
  );
}

export function plotStatusLabel(status: string): string {
  return PLOT_STATUS[status] ?? humanize(status);
}

export function itemKindLabel(id: string): string {
  if (id.startsWith('seed:')) return 'Seeds';
  if (id.startsWith('produce:')) return 'Produce';
  if (id.startsWith('fish:')) return 'Fish';
  if (id.startsWith('forage:')) return 'Forage';
  if (id.startsWith('ore:') || id.startsWith('gem:')) return 'Mined';
  return 'Other';
}

export function rodLabel(level: number): string {
  if (level <= 0) return 'No rod';
  if (level === 1) return 'Village rod';
  if (level === 2) return 'Advanced rod';
  return `Rod ${level}`;
}

const OUTFITS: Record<string, string> = {
  black: 'Tsoos dub (black)',
  pink: 'Tsoos paj (pink)',
};

const XAUV: Record<string, string> = {
  tub: 'Xauv (neck ring)',
  hli: 'Xauv hlau (plaque)',
  paj: 'Xauv paj (ceremonial)',
};

export function outfitLabel(id: string): string {
  return OUTFITS[id] ?? humanize(id);
}

export function xauvLabel(id: string): string {
  return XAUV[id] ?? humanize(id);
}

export function avatarLabel(avatar: string | null): string {
  if (avatar === 'boy') return 'Boy';
  if (avatar === 'girl') return 'Girl';
  if (!avatar) return 'Not chosen';
  return villageActivityLabel(avatar);
}

const TUTORIAL_STEPS: Record<number, string> = {
  0: 'Not started',
  1: 'Walk pending',
  2: 'Mailbox letter',
  3: 'Talk to Uncle',
  4: 'Done',
};

export function tutorialLabel(step: number): string {
  return TUTORIAL_STEPS[step] ?? `Step ${step}`;
}
